import { useEffect } from 'react';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import { isFieldVisible, getFieldConfig } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants/formConfigs';
import { removeRetailPricesForBusinessUnits } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/utils';
import { buildBUEntityConditions } from 'src/components/EntityPage/Filters/businessUnitFilters';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';

export const useValidateDataset = () => {
  const segments = useAppSelector((state) => {
    const { mySegments, teamSegments, teamSearchTermLists, mySearchTermLists } = state.segments;
    return [...mySegments, ...teamSegments, ...teamSearchTermLists, ...mySearchTermLists];
  });
  const { businessUnits, savedSearchesById } = useAppSelector((state) => state.segments);
  const {
    selections: { watch, setValue, setError },
    currentFormConfig
  } = usePresentationFormContext();
  const { showSnackbar } = useSnackbar();

  const entityType = watch('entityType');
  const entityId = watch('entityId');

  const { isLoading, data } = useAtlasV2TableData<{ metadata: any }>({
    fields: [
      {
        name: 'retailSales',
        displayName: 'RETAIL SALES'
      }
    ],
    groupByFieldName: 'brandId',
    indexName: 'sales',
    requestId: 'dataset-brands',
    itemsPerPage: 10,
    pageNumber: 1,
    sortFieldName: 'retailSales',
    suffixOverride: 'value',
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !!entityId.id
    },
    buildRequest: (builder) => {
      builder.removeConditionTermFilters((termFilter) => {
        return termFilter.fieldName === 'brandId';
      });
      if (entityType.id === 'segment') {
        const segment = segments.find((seg) => seg.id === entityId.id);
        if (segment) {
          if (segment.conditions.rangeFilters) {
            segment.conditions.rangeFilters.forEach((rangeFilter) => {
              builder.replaceConditionRangeFilter(rangeFilter.fieldName, rangeFilter.minValue, rangeFilter.maxValue);
            });
          }
          if (segment.conditions.termFilters) {
            segment.conditions.termFilters.forEach((termFilter) => {
              builder.replaceConditionTermFilter(termFilter.fieldName, termFilter.condition, termFilter.values);
            });
          }
        }
      } else if (entityType.id === 'businessunit') {
        const selectedBusinessUnit = businessUnits.find((businessUnit) => businessUnit.id === entityId.id);

        if (selectedBusinessUnit) {
          const nestedFilterConditions = buildBUEntityConditions(savedSearchesById, selectedBusinessUnit);

          builder.addPreCalculatedNestedFilterCondition(removeRetailPricesForBusinessUnits(nestedFilterConditions));
        }
      }

      return builder;
    }
  });

  useEffect(() => {
    if (!entityId.id || isLoading) return;

    // Determine minimum brands required based on config
    // If brandGroupings is visible (Top 10 brands mode), require 10 brands
    // If brandIds selection is enabled, use its minCount
    const showBrandGroupings = isFieldVisible(currentFormConfig, 'brandGroupings');
    const brandIdsConfig = getFieldConfig(currentFormConfig, 'brandIds');
    const brandIdsMinCount = brandIdsConfig.minCount ?? 0;
    
    let minBrandsRequired: number;
    if (showBrandGroupings) {
      // Top 10 brands mode - need at least 10 brands
      minBrandsRequired = 10;
    } else if (brandIdsMinCount > 0) {
      // Direct brand selection - need at least minCount brands
      minBrandsRequired = brandIdsMinCount;
    } else {
      // No brand requirement - skip validation
      return;
    }

    if (data.length < minBrandsRequired) {
      const errorMessage = `Please select a dataset with at least ${minBrandsRequired} brand${minBrandsRequired > 1 ? 's' : ''}`;
      showSnackbar({ message: errorMessage, type: 'error' });

      setError('entityId', { message: errorMessage });
      setValue('entityId', { id: '', name: '' });
    }
  }, [data, entityId.id, isLoading, currentFormConfig]);

  return {
    isValidating: isLoading,
  };
};
